(() => {
  'use strict';
  const runtimeKey = '__pbOriginWaterMotion';
  if (window[runtimeKey]) {
    window[runtimeKey].init(document);
    return;
  }
  const instances = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));

  function mount(root) {
    if (instances.has(root)) return;
    const toggle = root.querySelector('[data-pb-origin-toggle]');
    const label = root.querySelector('[data-pb-origin-label]');
    const trace = root.querySelector('[data-pb-origin-trace]');
    if (!toggle || !label || !trace) return;
    const current = root.querySelector('[data-pb-origin-current]');
    const art = root.querySelector('[data-pb-origin-flow="terraced"]');
    if (!art) return;
    const streams = Array.from(art.querySelectorAll('[data-pb-origin-stream]'));
    const receivers = Array.from(art.querySelectorAll('[data-pb-origin-receiver]'));
    // The gates sit progressively lower in the original full-page drawing.
    const gateStarts = [.46, .56, .66];
    let progress = 0;
    const paintFlow = (value) => {
      progress = clamp(value);
      trace.style.strokeDashoffset = (1 - progress).toFixed(4);
      root.style.setProperty('--pb-origin-trace-offset', (1 - progress).toFixed(4));
      streams.forEach((node, index) => {
        const p = clamp((progress - gateStarts[index]) / .12);
        node.style.strokeDasharray = '1 1';
        node.style.strokeDashoffset = (1 - p).toFixed(4);
        node.style.opacity = (p === 0 ? 0 : p === 1 ? .38 : .90).toFixed(3);
        const receive = clamp((p - .56) / .44);
        const receiver = receivers[index];
        if (receiver) {
          receiver.style.opacity = (Math.sin(receive * Math.PI) * .68).toFixed(3);
          receiver.style.transform = `scale(${(.72 + receive * .40).toFixed(3)})`;
        }
      });
      const active = gateStarts.findIndex((start) => progress >= start && progress < start + .12);
      root.dataset.pbOriginProgress = progress.toFixed(4);
      root.dataset.pbOriginFlowStage = String(active);
    };
    const smallCurrent = root.querySelector('[data-pb-origin-small-current]');
    const rippleNodes = Array.from(root.querySelectorAll('[data-pb-origin-pointer-ripple]'));
    const rippleAnimations = new Map();
    let locallyPaused = false;
    let visible = false;
    let frame = 0;
    let destroyed = false;
    let pointer = null;
    let rippleIndex = 0;
    let lastRippleTime = -Infinity;
    let lastRipplePoint = null;

    const isStill = () => locallyPaused || reduced.matches || document.documentElement.classList.contains('dm-paused');
    const isRunning = () => !destroyed && visible && !isStill() && !document.hidden;
    const clearRipples = () => {
      pointer = null;
      lastRippleTime = -Infinity;
      lastRipplePoint = null;
      rippleAnimations.forEach((animation) => animation.cancel());
      rippleAnimations.clear();
    };
    const stopFrame = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };
    const resetDrawing = () => {
      paintFlow(1);
      if (current) current.style.transform = 'translate(0px, 0px)';
      if (smallCurrent) smallCurrent.style.transform = 'translate(0px, 0px)';
    };
    const drawRipple = (rect) => {
      if (!pointer) return;
      const sample = pointer;
      pointer = null;
      if (!finePointer.matches || !rippleNodes.length) return;
      const x = sample.x - rect.left;
      const y = sample.y - rect.top;
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
      if (sample.time - lastRippleTime < 1100) return;
      if (lastRipplePoint && Math.hypot(x - lastRipplePoint.x, y - lastRipplePoint.y) < 48) return;
      const node = rippleNodes[rippleIndex % rippleNodes.length];
      if (typeof node.animate !== 'function') return;
      rippleIndex += 1;
      rippleAnimations.get(node)?.cancel();
      node.style.left = `${x.toFixed(1)}px`;
      node.style.top = `${y.toFixed(1)}px`;
      const animation = node.animate([
        { transform: 'translate(-50%, -50%) scale(.48)', opacity: 0 },
        { transform: 'translate(-50%, -50%) scale(.66)', opacity: .62, offset: .18 },
        { transform: 'translate(-50%, -50%) scale(1)', opacity: 0 }
      ], { duration: 1900, easing: 'cubic-bezier(.18,.65,.35,1)' });
      rippleAnimations.set(node, animation);
      lastRippleTime = sample.time;
      lastRipplePoint = { x, y };
    };
    const draw = () => {
      frame = 0;
      if (destroyed || isStill() || document.hidden || (observer && !visible)) return;
      const rect = root.getBoundingClientRect();
      const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
      if (!observer) visible = inViewport;
      root.dataset.pbOriginRunning = String(inViewport);
      if (!inViewport) {
        clearRipples();
        return;
      }
      const nextProgress = clamp((window.innerHeight * .85 - rect.top) / Math.max(1, rect.height - window.innerHeight * .1));
      paintFlow(nextProgress);
      const drift = nextProgress * 2 - 1;
      // Keep channels registered to the banks: only their water strokes advance.
      if (current) current.style.transform = 'translate(0px, 0px)';
      if (smallCurrent) smallCurrent.style.transform = `translate(${(drift * 10).toFixed(2)}px, ${(drift * -2).toFixed(2)}px)`;
      drawRipple(rect);
    };
    const requestDraw = () => {
      if (!frame && !destroyed && !isStill() && !document.hidden && (visible || !observer)) frame = requestAnimationFrame(draw);
    };
    const sync = () => {
      if (destroyed) return;
      const globalPause = document.documentElement.classList.contains('dm-paused');
      const still = isStill();
      const running = isRunning();
      root.dataset.pbOriginStill = String(still);
      root.dataset.pbOriginRunning = String(running);
      toggle.disabled = globalPause || reduced.matches;
      toggle.setAttribute('aria-pressed', String(still));
      label.textContent = reduced.matches ? toggle.dataset.reduced : globalPause ? toggle.dataset.global : locallyPaused ? toggle.dataset.play : toggle.dataset.pause;
      if (!running) {
        stopFrame();
        clearRipples();
      }
      if (reduced.matches) resetDrawing();
      if (!finePointer.matches) clearRipples();
      requestDraw();
    };
    const onToggle = () => {
      if (toggle.disabled) return;
      locallyPaused = !locallyPaused;
      sync();
    };
    const onPointerMove = (event) => {
      if (!isRunning() || !finePointer.matches || event.pointerType !== 'mouse' || event.buttons || event.isPrimary === false) return;
      if (event.target?.closest?.('.pb-origin__heading, .pb-origin__copy, .pb-origin__figure, a, button, input, select, textarea, [contenteditable]')) {
        pointer = null;
        return;
      }
      pointer = { x: event.clientX, y: event.clientY, time: event.timeStamp };
      requestDraw();
    };
    const onPointerLeave = () => { pointer = null; };
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      visible = entries.some((entry) => entry.target === root && entry.isIntersecting);
      sync();
    }, { rootMargin: '0px', threshold: 0 }) : null;
    const classObserver = new MutationObserver(sync);
    classObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    const onScroll = requestDraw;
    paintFlow(reduced.matches ? 1 : 0);
    toggle.hidden = false;
    toggle.addEventListener('click', onToggle);
    root.addEventListener('pointermove', onPointerMove, { passive: true });
    root.addEventListener('pointerleave', onPointerLeave, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    const layoutObserver = 'ResizeObserver' in window ? new ResizeObserver(requestDraw) : null;
    layoutObserver?.observe(root);
    document.fonts?.ready.then(requestDraw);
    document.addEventListener('visibilitychange', sync);
    reduced.addEventListener('change', sync);
    finePointer.addEventListener('change', sync);
    if (observer) observer.observe(root);
    sync();
    instances.set(root, () => {
      destroyed = true;
      observer?.disconnect();
      layoutObserver?.disconnect();
      classObserver.disconnect();
      stopFrame();
      clearRipples();
      trace.style.removeProperty('stroke-dashoffset');
      current?.style.removeProperty('transform');
      smallCurrent?.style.removeProperty('transform');
      streams.forEach((node) => {
        node.style.removeProperty('stroke-dasharray');
        node.style.removeProperty('stroke-dashoffset');
        node.style.removeProperty('opacity');
      });
      receivers.forEach((node) => { node.style.removeProperty('opacity'); node.style.removeProperty('transform'); });
      root.style.removeProperty('--pb-origin-trace-offset');
      delete root.dataset.pbOriginProgress;
      delete root.dataset.pbOriginFlowStage;
      delete root.dataset.pbOriginStill;
      root.dataset.pbOriginRunning = 'false';
      toggle.hidden = true;
      toggle.removeEventListener('click', onToggle);
      root.removeEventListener('pointermove', onPointerMove);
      root.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', sync);
      reduced.removeEventListener('change', sync);
      finePointer.removeEventListener('change', sync);
      instances.delete(root);
    });
  }
  const init = (scope = document) => {
    if (scope.matches?.('[data-pb-origin]')) mount(scope);
    scope.querySelectorAll('[data-pb-origin]').forEach(mount);
  };
  window[runtimeKey] = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((cleanup, root) => { if (event.target.contains(root)) cleanup(); });
  });
})();
